import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clientService } from '../../services/clientService';
import { useToast } from '../../context/ToastContext';
import AdminHeader from '../../components/admin/AdminHeader';

const EditClientPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: ''
    });

    useEffect(() => {
        if (!id) {
            navigate('/admin/clients');
            return;
        }
        
        const loadClient = async () => {
            try {
                const client = await clientService.getClientByIdAdmin(parseInt(id));
                setFormData({
                    firstName: client.firstName,
                    lastName: client.lastName,
                    email: client.email,
                    phoneNumber: client.phoneNumber || ''
                });
            } catch (error) {
                console.error('Error loading client:', error);
                showToast('No se pudo cargar la información del cliente', 'error');
                navigate('/admin/clients');
            } finally {
                setLoading(false);
            }
        };
        
        loadClient();
    }, [id, navigate, showToast]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.email || !formData.firstName || !formData.lastName) {
            showToast('El nombre, apellido y email son obligatorios', 'error');
            return;
        }

        try {
            setSubmitting(true);
            await clientService.updateClientAdmin(parseInt(id!), formData);
            showToast('Cliente actualizado exitosamente', 'success');
            navigate('/admin/clients');
        } catch (error: any) {
            console.error('Error updating client:', error);
            showToast(error.response?.data?.message || 'Error al actualizar el cliente', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <AdminHeader title="✏️ Editar Cliente" backPath="/admin/clients" />
            
            <div className="container fade-in" style={{ maxWidth: '600px', margin: '0 auto' }}>
                <div className="card p-xl">
                    {loading ? (
                        <div className="text-center">
                            <div className="spinner m-auto mb-md"></div>
                            <p>Cargando datos...</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="form-group mb-md">
                                <label className="form-label" htmlFor="firstName">Nombre</label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    className="form-input"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            
                            <div className="form-group mb-md">
                                <label className="form-label" htmlFor="lastName">Apellido</label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    className="form-input"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-group mb-md">
                                <label className="form-label" htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    className="form-input"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-group mb-lg">
                                <label className="form-label" htmlFor="phoneNumber">WhatsApp</label>
                                <input
                                    type="tel"
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    className="form-input"
                                    value={formData.phoneNumber}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="flex gap-sm">
                                <button 
                                    type="button" 
                                    className="btn btn-outline" 
                                    style={{ flex: 1 }}
                                    onClick={() => navigate('/admin/clients')}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn btn-primary" 
                                    style={{ flex: 1 }}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Guardando...' : 'Guardar Cambios'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </>
    );
};

export default EditClientPage;
